import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2, Settings, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@marine/ui/alert-dialog';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@marine/ui/card';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Separator } from '@marine/ui/separator';
import { typedResolver } from '@/lib/typedResolver';
import {
  usePlatformSettings,
  useUpdatePlatformSettings,
} from '@/modules/platform/services/platformSettingsApi';

const licenseSchema = z.object({
  syncfusion_license_key: z.string().min(1, 'Enter a license key'),
});

type LicenseFormValues = z.output<typeof licenseSchema>;

/**
 * Platform → Platform Settings (#824).
 *
 * Manages platform-owned third-party client keys — currently the Syncfusion
 * license key served to every tenant SPA via the #app-config island. The API
 * only ever returns a masked tail (`…abcd`) + `has_key`; the input below is
 * never prefilled with key material.
 */
export function PlatformSettingsPage() {
  const { data, isLoading } = usePlatformSettings();
  const updateMutation = useUpdatePlatformSettings();

  const form = useForm<LicenseFormValues>({
    resolver: typedResolver(licenseSchema),
    defaultValues: { syncfusion_license_key: '' },
  });

  const onSubmit = (values: LicenseFormValues) => {
    updateMutation.mutate(
      { syncfusion_license_key: values.syncfusion_license_key },
      {
        onSuccess: () => {
          toast.success('Syncfusion license key saved');
          form.reset();
        },
        onError: () => {
          toast.error('Failed to save Syncfusion license key');
        },
      },
    );
  };

  const handleClear = () => {
    updateMutation.mutate(
      { syncfusion_license_key: null },
      {
        onSuccess: () => {
          toast.success('Syncfusion license key cleared');
          form.reset();
        },
        onError: () => {
          toast.error('Failed to clear Syncfusion license key');
        },
      },
    );
  };

  return (
    <div className="space-y-6 p-5 pb-8">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Settings className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-lg font-semibold">Platform Settings</h1>
          <p className="text-xs text-muted-foreground">
            Platform-managed third-party client keys served to tenant apps at runtime.
          </p>
        </div>
      </div>

      {/* Security notice */}
      <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="text-xs text-amber-900 dark:text-amber-200">
          The license key is encrypted at rest in the landlord database. Raw key material is
          never returned over the API after it's saved — display values show only the last 4
          characters. Changes reach tenant apps within ~5 minutes (cached read), no redeploy.
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <div className="text-sm font-medium">Loading platform settings…</div>
          </div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Syncfusion License</CardTitle>
                <CardDescription>
                  Powers the rich-text editors in the tenant apps. Rotating the key here
                  applies to all tenants without a rebuild.
                </CardDescription>
              </div>
              {data?.has_key ? (
                <Badge variant="success" appearance="light" size="sm">
                  Configured
                </Badge>
              ) : (
                <Badge variant="secondary" size="sm">
                  Not configured
                </Badge>
              )}
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="syncfusion_license_key">Syncfusion license key</Label>
                <p className="text-xs text-muted-foreground">
                  {data?.has_key ? `Current: ${data.syncfusion_license_masked}` : 'No key set'}
                </p>
                <Input
                  id="syncfusion_license_key"
                  type="password"
                  placeholder={data?.has_key ? 'Enter a replacement key' : 'Paste the license key'}
                  autoComplete="off"
                  {...form.register('syncfusion_license_key')}
                  disabled={updateMutation.isPending}
                />
                {form.formState.errors.syncfusion_license_key && (
                  <p className="text-sm text-destructive" role="alert">
                    {form.formState.errors.syncfusion_license_key.message}
                  </p>
                )}
              </div>

              {updateMutation.isError && (
                <p className="text-sm text-destructive" role="alert">
                  Saving failed — check your connection and try again.
                </p>
              )}

              <div className="flex justify-end gap-2">
                {data?.has_key && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={updateMutation.isPending}
                      >
                        Clear key
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clear the Syncfusion license key?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tenants will fall back to the VITE_SYNCFUSION_LICENSE environment
                          value (if any) within ~5 minutes. Editors keep working either way —
                          without a valid license they show Syncfusion&apos;s trial banner.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleClear}>
                          Clear the key
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <Button type="submit" size="sm" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  ) : null}
                  Save
                </Button>
              </div>
            </form>

            <Separator className="my-4" />

            <div className="text-xs text-muted-foreground">
              {data?.updated_at ? (
                <>
                  Last updated {new Date(data.updated_at).toLocaleString()}
                  {data.updated_by ? ` by ${data.updated_by.full_name}` : ''}
                </>
              ) : (
                'No key set yet.'
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
